import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Y from "yjs";
import { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate } from "y-protocols/awareness";

/**
 * useYjsRoom — Phase-2 CRDT layer for ELPM Studio's collab rooms.
 *
 * Owns ONE Y.Doc per room and keeps it in sync with every peer by piggybacking
 * on the existing /api/collab WebSocket. We ship Yjs binary updates as base64
 * inside the same envelope the rest of collab uses:
 *
 *     { type: "crdt:update",  payload: { b64: "..." } }            // diff
 *     { type: "crdt:request", payload: {}, to: peerId }            // hello
 *     { type: "crdt:state",   payload: { b64: "..." }, to: peerId } // full reply
 *
 * Why WS-only (no WebRTC binary path) here:
 *  - Yjs updates for transport + volume + mute fit easily under 200 bytes —
 *    a base64 round-trip is cheap.
 *  - The WS path works even before data channels open (i.e. immediately on
 *    room join), so late-joiners get full state without extra plumbing.
 *  - We can swap to RTCDataChannel.send(ArrayBuffer) in a future iteration
 *    without changing the public API of this hook.
 *
 * Loop-back prevention:
 *   When we apply a remote update, we tag it with the LOCAL_ORIGIN sentinel.
 *   Observers should check `tx.origin !== LOCAL_ORIGIN` before bridging into
 *   the audio engine, otherwise echo-back creates an infinite update loop.
 *   Local mutations should also be wrapped with `doc.transact(fn, REMOTE_ORIGIN)`
 *   when applying remote events into shared types we want the engine to ignore.
 *
 * Public surface:
 *   - doc         : Y.Doc          (use .getMap('transport') / .getMap('channels'))
 *   - ready       : boolean        (true once doc exists, even before first sync)
 *   - sync        : boolean        (true after we've at least observed one peer state)
 *   - LOCAL_ORIGIN: Symbol         (use as the `origin` arg to doc.transact when
 *                                   applying incoming engine state TO the doc)
 *   - REMOTE_ORIGIN: Symbol        (use to mark applies of remote updates so the
 *                                   engine-bridge observer can skip them)
 */

export const LOCAL_ORIGIN = Symbol("yjs:local");
export const REMOTE_ORIGIN = Symbol("yjs:remote");

// Browser-portable base64 ↔ Uint8Array. Yjs updates are short so the
// constant-overhead is negligible.
function bytesToB64(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
function b64ToBytes(b64) {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export default function useYjsRoom({ me, peers, send, enabled, mesh }) {
  // `ready` defaults to true because `doc` is created synchronously via
  // useMemo below, so the hook is usable from the very first render. The
  // previous false-then-true-in-effect dance triggered React 19-strict's
  // `set-state-in-effect` warning and added a redundant re-render. The
  // teardown setReady(false) was a no-op anyway (component is gone).
  const [ready] = useState(true);
  const [sync, setSync] = useState(false);
  // Counter of how many updates were last shipped via RTC vs WS — surfaced
  // through the returned `stats` so the UI can show "fast" mode when RTC
  // is carrying the bulk of traffic.
  const [stats, setStats] = useState({ rtcSent: 0, wsSent: 0, rtcRecv: 0, wsRecv: 0 });

  // Stable Y.Doc per hook instance. We create one and dispose on teardown.
  // useMemo with [] would normally be a hot take, but the doc identity is
  // intentionally bound to the lifetime of the hook (which is mounted once
  // per room session via the parent).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const doc = useMemo(() => new Y.Doc(), []);
  // Awareness — ephemeral presence layer (cursor positions, names, colors).
  // Lives ALONGSIDE the doc rather than inside it: y-protocols/awareness has
  // built-in dead-peer eviction via timestamps + outliveTimeout, which is the
  // right semantic for "user A's cursor" (should disappear on disconnect)
  // rather than the LWW persistence of regular Y types.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const awareness = useMemo(() => new Awareness(doc), [doc]);
  const [awarenessStates, setAwarenessStates] = useState(() => new Map());

  // Stable refs so the inbound-message handler and outbound updates never
  // restate-loop the parent.
  const sendRef = useRef(send);
  const meRef = useRef(me);
  const peersRef = useRef(peers);
  const meshRef = useRef(mesh);
  const seenPeersRef = useRef(new Set());
  useEffect(() => { sendRef.current = send; }, [send]);
  useEffect(() => { meRef.current = me; }, [me]);
  useEffect(() => { peersRef.current = peers; }, [peers]);
  useEffect(() => { meshRef.current = mesh; }, [mesh]);

  // ─── Outbound: ship every local update to the room. ────────────────────
  // Yjs fires `doc.on("update", (update, origin) => ...)` for both local
  // changes AND remotely-applied changes. We MUST skip the latter or we'll
  // ping-pong them between peers forever.
  //
  // Dual-path delivery (Phase 2.5):
  //   1. RTC: if the mesh has open data channels, send the raw bytes there
  //      first — sub-30ms peer-to-peer, no server hop, no base64 round-trip.
  //   2. WS:  ALSO send via the WebSocket *if* there are collab peers we
  //      didn't reach via RTC (mesh capped at 4; rooms can hold 16). Yjs
  //      updates are idempotent so duplicate delivery to mesh peers is a
  //      cheap no-op on the receiver.
  useEffect(() => {
    if (!enabled) return undefined;
    const onUpdate = (update, origin) => {
      if (origin === REMOTE_ORIGIN) return;  // came from a peer — don't echo
      const mesh = meshRef.current;
      const peers = peersRef.current || [];
      const me = meRef.current;
      const otherPeerCount = peers.filter((p) => p?.id && p.id !== me?.id).length;

      // 1. RTC fast path — broadcast raw bytes to every open data channel.
      let rtcReached = 0;
      try {
        if (mesh && typeof mesh.sendBinary === "function") {
          rtcReached = mesh.sendBinary(update) || 0;
        }
      } catch (e) {
        console.warn("[yjs] sendBinary threw", e);
      }

      // 2. WS fallback — send if any collab peer is NOT in the open-channel set.
      //    Also covers the "no mesh yet" boot phase where the RTC handshake
      //    hasn't completed for any peer.
      const send = sendRef.current;
      let wsSent = false;
      if (send && rtcReached < otherPeerCount) {
        try {
          send("crdt:update", { b64: bytesToB64(update) });
          wsSent = true;
        } catch (e) {
          console.warn("[yjs] failed to ship update", e);
        }
      }

      // Telemetry (one render at most per update batch — Yjs batches transactions).
      setStats((s) => ({
        ...s,
        rtcSent: s.rtcSent + (rtcReached > 0 ? 1 : 0),
        wsSent:  s.wsSent  + (wsSent ? 1 : 0),
      }));
    };
    doc.on("update", onUpdate);
    return () => doc.off("update", onUpdate);
  }, [doc, enabled]);

  /** Apply an incoming binary Yjs update, tagged as REMOTE so the outbound
   *  observer above doesn't bounce it back. */
  const applyRemoteUpdate = useCallback((bytes, path = "ws") => {
    if (!(bytes instanceof Uint8Array)) return;
    try {
      Y.applyUpdate(doc, bytes, REMOTE_ORIGIN);
      if (!sync) setSync(true);
      setStats((s) => ({
        ...s,
        rtcRecv: s.rtcRecv + (path === "rtc" ? 1 : 0),
        wsRecv:  s.wsRecv  + (path === "ws"  ? 1 : 0),
      }));
    } catch (e) {
      console.warn("[yjs] applyUpdate failed", e);
    }
  }, [doc, sync]);

  /** Public entry point for raw Yjs bytes arriving via the WebRTC data
   *  channel. The mesh hook calls this when a peer sends an ArrayBuffer
   *  (set by useYjsRoom's outbound `mesh.sendBinary(update)`). */
  const applyRemoteBinary = useCallback((bytes) => {
    applyRemoteUpdate(bytes, "rtc");
  }, [applyRemoteUpdate]);

  // ─── Awareness (cursor / presence) ─────────────────────────────────────
  // Ships its own diffs over the same WS as `awareness:update` envelopes.
  // No backend changes — the existing collab broadcast carries arbitrary
  // typed messages.
  useEffect(() => {
    if (!enabled) return undefined;
    const onAwarenessUpdate = ({ added, updated, removed }, origin) => {
      // Skip awareness updates that came in from the wire — we'd echo them.
      if (origin === "remote") return;
      const changedClients = [...added, ...updated, ...removed];
      if (changedClients.length === 0) return;
      try {
        const update = encodeAwarenessUpdate(awareness, changedClients);
        sendRef.current?.("awareness:update", { b64: bytesToB64(update) });
      } catch (e) {
        console.warn("[yjs] awareness encode failed", e);
      }
    };
    const onChange = () => {
      // Re-render trigger — flatten the awareness states to a plain Map for React.
      setAwarenessStates(new Map(awareness.getStates()));
    };
    awareness.on("update", onAwarenessUpdate);
    awareness.on("change", onChange);
    return () => {
      awareness.off("update", onAwarenessUpdate);
      awareness.off("change", onChange);
    };
  }, [awareness, enabled]);

  /** Mutate this client's awareness state. Merges with existing fields. */
  const setLocalAwareness = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    const current = awareness.getLocalState() || {};
    awareness.setLocalState({ ...current, ...patch });
  }, [awareness]);

  // Drop doc when the hook unmounts so we don't leak observers between
  // join/leave cycles within the same browser tab.
  useEffect(() => {
    return () => {
      try { doc.destroy(); } catch (e) { void e; }
    };
  }, [doc]);

  /** Dispatch incoming WS messages relevant to CRDT sync. Call this from
   *  the parent's collab.onMessage path. Handles 3 envelope types:
   *
   *    crdt:update   → apply the binary diff to our doc
   *    crdt:request  → reply with our full state (used when a peer joins)
   *    crdt:state    → apply the full state another peer sent us
   */
  const handleSignal = useCallback((msg) => {
    if (!msg || typeof msg !== "object") return;
    const t = msg.type;
    if (t === "crdt:update") {
      const b64 = msg.payload?.b64;
      if (typeof b64 === "string") applyRemoteUpdate(b64ToBytes(b64));
      return;
    }
    if (t === "crdt:request") {
      // Reply with our full doc state, directed back to the requester.
      const fromId = msg.from_id;
      if (!fromId) return;
      try {
        const state = Y.encodeStateAsUpdate(doc);
        sendRef.current?.("crdt:state", { b64: bytesToB64(state) }, { to: fromId });
      } catch (e) {
        console.warn("[yjs] state encode failed", e);
      }
      return;
    }
    if (t === "crdt:state") {
      const b64 = msg.payload?.b64;
      if (typeof b64 === "string") applyRemoteUpdate(b64ToBytes(b64));
      return;
    }
    if (t === "awareness:update") {
      // Inbound awareness diff — apply with origin='remote' so our outbound
      // observer above doesn't echo back.
      const b64 = msg.payload?.b64;
      if (typeof b64 !== "string") return;
      try {
        applyAwarenessUpdate(awareness, b64ToBytes(b64), "remote");
      } catch (e) {
        console.warn("[yjs] awareness apply failed", e);
      }
      return;
    }
  }, [doc, awareness, applyRemoteUpdate]);

  // When a NEW peer appears in the roster, request their full state once
  // (only one of the two needs to ask — the lower-id peer requests, the
  // higher-id peer responds). Reusing the same polite-peer pattern from the
  // RTC mesh keeps the design consistent.
  useEffect(() => {
    if (!enabled || !me?.id || !Array.isArray(peers)) return;
    const myId = String(me.id);
    peers.forEach((p) => {
      if (!p?.id || p.id === me.id) return;
      if (seenPeersRef.current.has(p.id)) return;
      seenPeersRef.current.add(p.id);
      // Only the LEXICOGRAPHICALLY-LOWER id sends the request — avoids
      // double-state-thrash when two peers see each other simultaneously.
      if (myId < String(p.id)) {
        sendRef.current?.("crdt:request", {}, { to: p.id });
      }
      // ALWAYS announce our current awareness state to the new peer (cursor
      // position + display name). Both sides do this so each end sees the
      // other's cursor instantly without waiting for the next pulse.
      try {
        const localId = awareness.clientID;
        const update = encodeAwarenessUpdate(awareness, [localId]);
        sendRef.current?.("awareness:update", { b64: bytesToB64(update) }, { to: p.id });
      } catch (e) {
        console.warn("[yjs] awareness greet failed", e);
      }
    });
    // Drop peers that left so a re-join triggers a fresh state request.
    const here = new Set(peers.map((p) => p.id));
    [...seenPeersRef.current].forEach((id) => {
      if (!here.has(id)) seenPeersRef.current.delete(id);
    });
  }, [peers, me, enabled, awareness]);

  // When `enabled` flips off (left room), clear the doc completely so the
  // next join doesn't carry over stale state.
  useEffect(() => {
    if (enabled) return undefined;
    const seenPeers = seenPeersRef.current;
    // Reset doc contents — destroy + recreate is overkill; transact a no-op
    // and zero out the maps we own.
    return () => {
      try {
        doc.transact(() => {
          doc.getMap("transport").clear();
          const channels = doc.getMap("channels");
          channels.forEach((_, k) => channels.delete(k));
          doc.getMap("fx").clear();
          const sidechain = doc.getMap("sidechain");
          sidechain.forEach((_, k) => sidechain.delete(k));
        }, REMOTE_ORIGIN);
        // Clear our own awareness state too so we don't lurk as a phantom
        // cursor on the other peer's screen for the outliveTimeout window.
        awareness.setLocalState(null);
      } catch (e) { void e; }
      seenPeers.clear();
      setSync(false);
      setAwarenessStates(new Map());
    };
  }, [enabled, doc, awareness]);

  return {
    doc,
    awareness,
    awarenessStates,
    setLocalAwareness,
    ready,
    sync,
    stats,
    handleSignal,
    applyRemoteBinary,
    LOCAL_ORIGIN,
    REMOTE_ORIGIN,
    /** Convenience writer that wraps `doc.transact` with the right origin
     *  so the observer-bridge in the parent doesn't double-apply. */
    transact: useCallback((fn) => {
      try { doc.transact(fn, LOCAL_ORIGIN); }
      catch (e) { console.warn("[yjs] transact failed", e); }
    }, [doc]),
  };
}
